/**
 * Admin Link Component
 * 
 * Link component for admin actions.
 * Only renders if the user is authenticated as an admin.
 * Cloudflare Access protects /admin/* routes at the edge.
 * 
 * FAIL CLOSED: Button is hidden by default and only shows after successful auth check.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@mui/material';
import { useAdminAuth } from '../../hooks/useAdminAuth';

// DEBUG: Log when module is loaded
console.log('[AdminLink] Module loaded at:', Date.now());

interface AdminLinkProps {
  /** Link text */
  children: React.ReactNode;
  /** Link to path */
  to: string;
  /** Button variant */
  variant?: 'text' | 'outlined' | 'contained';
  /** Button color */
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
}

/**
 * Admin Link Component
 * 
 * FAIL CLOSED: Only renders if user is authenticated as admin.
 * Returns null (hidden) while loading or if not authenticated.
 * This ensures the button never appears before auth check completes.
 */
const AdminLink: React.FC<AdminLinkProps> = ({
  children,
  to,
  variant = 'text',
  color = 'primary',
}) => {
  // CRITICAL DEBUG: This should ALWAYS log if component is rendered
  console.log('[AdminLink] Component RENDERED - to:', to, 'time:', Date.now());
  
  const { isAuthenticated, isLoading, error } = useAdminAuth();
  
  // Debug logging
  console.log('[AdminLink] Render - isAuthenticated:', isAuthenticated, 'isLoading:', isLoading, 'error:', error);

  // FAIL CLOSED: Don't render anything while checking authentication
  // This prevents the button from appearing before auth check completes
  if (isLoading) {
    return null;
  }

  // FAIL CLOSED: Don't render if there was an error (treat as not authenticated)
  if (error) {
    return null;
  }

  // FAIL CLOSED: Only render if explicitly authenticated
  // isAuthenticated starts as false and only becomes true after successful 200 JSON response
  if (!isAuthenticated) {
    return null;
  }

  // Only render button if we have explicit authentication confirmation
  return (
    <Button component={Link} to={to} variant={variant} color={color}>
      {children}
    </Button>
  );
};

export default AdminLink;

